import {
  Component, EventEmitter, Inject, OnDestroy, OnInit,
  Output
} from '@angular/core';
import {
  filter,
  map, Observable, of, Subject, switchMap, take, takeUntil,
  tap,
  timer
} from 'rxjs';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl, FormGroup, ValidationErrors, Validators
} from '@angular/forms';
import { ReferenceDataHttpService, ReferenceDatum } from '@sondermind/data-access/reference-data';
import {
  ILocationForm, IQuickIntakeForm,
  QuickIntakeFormValues
} from '@sondermind/features/quick-intake';
import { ProviderNetworkCompanyStore } from '@sondermind/data-access/provider-network-company';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { PaymentTypes } from '@sondermind/features/public-facing/session-request';
import { IInsurance } from '@sondermind/utilities/models-provider-network-company';
import { IState, StatesServedStore } from '@sondermind/data-access/public-facing/states-served';
import { SharedMasks } from '@sondermind/utilities/text-masks';
import { SimpleDateValidator } from '@sondermind/utilities/tools/validators/simple-date.validator';
import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';
import { PhoneValidator } from '@sondermind/utilities/tools/validators/phone.validator';
import { PhoneAsyncValidator } from '@sondermind/data-access/phone-validator';
import { RadioOption } from '@sondermind/iris/radio-group';
import { TextareaSizes } from '@sondermind/iris/text-area';
import {
  FlowsLaunchDarklyService, LAUNCH_DARKLY_SERVICE,
  LDBooleanFeatureFlags
} from '@sondermind/launch-darkly';
import { SegmentService } from '@sondermind/segment';
import { InfoLinks } from '@sondermind/core/enums/info-links.enum';
import { FormRefData } from '../../models/enums/form-ref-data.enum';
import { QuickIntakeFormState } from '../../models/enums/quick-intake-form-state.enum';
import { QuickIntakeStore } from '../../stores/quick-intake.store';
import {
  IQuickIntakeSegmentSchema, IQuickLeadFormChangedData, QuickIntakeSegmentEvents
} from '../../models/segment/quick-intake-segment-schema.interface';

type QuickIntakeFormKeys = keyof IQuickIntakeForm;

@Component({
  selector: 'quick-intake-form',
  templateUrl: './quick-intake-form.component.html',
  styleUrls: ['./quick-intake-form.component.scss']
})
export class QuickIntakeFormComponent implements OnInit, OnDestroy {
  constructor(
    private refDataHttp: ReferenceDataHttpService,
    private pncStore: ProviderNetworkCompanyStore,
    private statesServedStore: StatesServedStore,
    private phoneAsyncValidator: PhoneAsyncValidator,
    private quickIntakeStore: QuickIntakeStore,
    private segmentService: SegmentService<IQuickIntakeSegmentSchema>,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: FlowsLaunchDarklyService,
  ) {
    this.setupForm();
  }

  @Output() formStateChange = new EventEmitter<QuickIntakeFormState>();
  private _currentFormState: QuickIntakeFormState = QuickIntakeFormState.FORM;
  get currentFormState(): QuickIntakeFormState {
    // eslint-disable-next-line no-underscore-dangle
    return this._currentFormState;
  }
  set currentFormState(value: QuickIntakeFormState) {
    // eslint-disable-next-line no-underscore-dangle
    this._currentFormState = value;
    this.formStateChange.emit(value);
  }

  acceptedInsurances: Array<DropdownOption<number>> = [];
  availablePaymentTypes: Array<DropdownOption<string>> = [];
  genderTypes: Array<RadioOption<number>> = [];
  readinessOptions: Array<RadioOption<string>> = [];
  sessionPreferenceTypes: Array<RadioOption<string | null>> = [];
  acceptedStates: IState[] = [];
  notAvailableInYourState: boolean = false;
  showInsurancePlans: boolean = false;
  medicaidPilotOn: boolean = false;
  insuranceFormLabel: string = '';
  allInsurances: IInsurance[] = [];

  paymentTypes = PaymentTypes;
  textareaSizes = TextareaSizes;
  quickIntakeFormState = QuickIntakeFormState;

  comingToYourStateLink = InfoLinks.CLIENT_COMING_TO_YOUR_STATE;
  birthdayMask = SharedMasks['birthdayMask'].ngxMask;
  phoneMask = SharedMasks['phoneMask'].ngxMask;
  quickIntakeForm!: FormGroup<IQuickIntakeForm>;
  locationFormControl!: FormControl<string | null>;

  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.fireFormStartedSegmentEvent();
    this.setupFormChangedSubscriptions();
    this.loadLdFlags();
    this.loadGenders();
    this.loadSessionPreferences();
    this.loadReadiness();
    this.loadStates();
    this.setupPaymentTypeSubscription();
    this.setupLocationSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setupForm(): void {
    this.quickIntakeForm = new FormGroup<IQuickIntakeForm>({
      openPreferences: new FormControl<string | null>(null, [Validators.required]),
      location: new FormGroup<ILocationForm>({
        name: new FormControl<string | null>(null, [Validators.required]),
        lat: new FormControl<number | null>(null, [Validators.required]),
        lng: new FormControl<number | null>(null, [Validators.required]),
        address: new FormControl<string | null>(null, [Validators.required]),
        state: new FormControl<string | null>(null, [Validators.required]),
        zip: new FormControl<string | null>(null)
      }),
      paymentType: new FormControl<PaymentTypes | null>({ value: null, disabled: true }, [Validators.required]),
      insurance: new FormControl<number | null>({ value: null, disabled: true }),
      firstName: new FormControl<string | null>(null, [Validators.required]),
      lastName: new FormControl<string | null>(null, [Validators.required]),
      birthdate: new FormControl<string | null>(null, [Validators.required, SimpleDateValidator.format]),
      email: new FormControl<string | null>(null, [Validators.required, EmailValidator.format]),
      phone: new FormControl<string | null>(
        null,
        {
          validators: [Validators.required, PhoneValidator.format],
          asyncValidators: [this.phoneAsyncValidator.validate.bind(this.phoneAsyncValidator)]
        }
      ),
      genderPreference: new FormControl<number | null>(null),
      sessionFormatPreference: new FormControl<string | null>(null),
      contactSmsAllowed: new FormControl<boolean | null>(true),
      commPrefs: new FormGroup({
        commPrefsPhone: new FormControl<boolean | null>(true),
        commPrefsSms: new FormControl<boolean | null>(true)
      }, Validators.required),
      medicationManagement: new FormControl<boolean>(false),
      readiness: new FormControl<string | null>(null, [Validators.required])
    });

    this.locationFormControl = new FormControl<string | null>(
      {
        value: null,
        disabled: false
      },
      {
        updateOn: 'blur',
        validators: [Validators.required],
        asyncValidators: [this.locationValidator()]
      }
    );
  }

  locationValidator(): AsyncValidatorFn {
    // timer needed because google.maps.api has a delay from when location is selected to when the validator is called
    return (control: AbstractControl): Observable<ValidationErrors | null> => timer(300).pipe(
      switchMap(() => {
        if (control.value === this.quickIntakeForm.value?.location?.address) return of(null);

        this.resetPaymentTypeAndInsurance();
        return of({ invalidLocation: true });
      })
    );
  }

  loadRefDataObservable(dataType: FormRefData, skipOptionList: string[] = []): Observable<ReferenceDatum[]> {
    const skipOptions = skipOptionList.map((o) => o.toLowerCase());

    return this.refDataHttp.list(dataType,
      {
        filter:
        [{ key: 'hidden', value: 'false' }],
        sort: [{ key: 'order_number', asc: true }]
      }
    ).pipe(
      map((e) => e.data.filter((o) => !skipOptions.includes(o.title.toLowerCase())))
    );
  }

  loadLdFlags(): void {
    this.launchDarklyService.checkFlagEnabled$(LDBooleanFeatureFlags.MEDICAID_PILOT).subscribe((result) => {
      this.medicaidPilotOn = result;
    });
  }

  loadGenders(): void {
    const skipOption = ['prefer not to answer'];
    this.loadRefDataObservable(FormRefData.GENDER, skipOption).pipe(
      map((data) => data.map((option) => ({
        name: option.title, value: option.id
      })))
    ).subscribe({
      next: (genderTypes) => {
        this.genderTypes = genderTypes;
      }
    });
  }

  loadSessionPreferences(): void {
    this.sessionPreferenceTypes = [
      { name: 'Virtual only', value: 'video' },
      { name: 'In-person only', value: 'inPerson' },
      { name: 'Both', value: null }
    ];
  }

  loadReadiness(): void {
    this.readinessOptions = [
      { name: 'I am ready to find a therapist and schedule a session.', value: 'action' },
      { name: 'I am just starting to explore my options.', value: 'contemplation' }
    ];
  }

  loadStates(): void {
    this.statesServedStore.stateList$().subscribe({
      next: (states) => {
        this.acceptedStates = states;
      }
    });
  }

  locationSelected(selectedAddress: google.maps.places.PlaceResult): void {
    if (!(selectedAddress?.geometry && selectedAddress?.formatted_address && selectedAddress?.name)) return;

    const state = selectedAddress?.address_components?.find(
      (x) => x.types.includes('administrative_area_level_1'))?.short_name;
    if (!state) return;

    const zip = selectedAddress?.address_components?.find(
      (x) => x?.types?.includes('postal_code'))?.long_name;

    this.quickIntakeForm.controls.location.patchValue({
      name: selectedAddress.name,
      lat: selectedAddress.geometry?.location?.lat(),
      lng: selectedAddress.geometry?.location?.lng(),
      address: selectedAddress.formatted_address,
      state: state ?? '',
      zip: zip ?? null
    });

    this.locationFormControl.patchValue(selectedAddress.formatted_address);
  }

  setupLocationSubscription(): void {
    this.quickIntakeForm.controls.location.valueChanges.pipe(
      tap((location) => {
        this.checkIfStateIsValid(location.state ?? '');
      }),
      switchMap((location) => this.getInsuranceOptionsByState(location.state ?? '')),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  setupPaymentTypeSubscription(): void {
    this.quickIntakeForm.controls.paymentType.valueChanges.pipe(
      tap((paymentType) => {
        if (!paymentType) return;
        this.updateAcceptedInsurances(paymentType);
      }),
      takeUntil(this.destroyed$)
    ).subscribe();
  }

  getInsuranceOptionsByState(state: string): Observable<IInsurance[]> {
    // if state is blank it will return all results
    return this.pncStore.getInsuranceOptionsByState$({ state, forceReload: false, includeCashPay: true }).pipe(
      tap((insurances) => {
        this.allInsurances = insurances;
        this.updatePaymentTypes(insurances);
      })
    );
  }

  checkIfStateIsValid(state: string): void {
    const foundState = this.acceptedStates.find((x) => x.abbreviation === state);
    if (foundState) {
      this.notAvailableInYourState = false;
      this.quickIntakeForm.enable({ emitEvent: false });
      this.resetPaymentTypeAndInsurance(false);
    } else {
      this.notAvailableInYourState = true;
      this.quickIntakeForm.disable({ emitEvent: false });
      this.resetPaymentTypeAndInsurance();
    }
  }

  updatePaymentTypes(insurances: IInsurance[]): void {
    this.availablePaymentTypes = [];
    const options: Set<string> = new Set();
    insurances.forEach((insurance) => {
      if (insurance.isEap) {
        options.add(PaymentTypes.EAP);
      }
      if (insurance.isMedicare) {
        options.add(PaymentTypes.MEDICARE);
      }
      if (insurance.isPrivateInsurance) {
        options.add(PaymentTypes.INSURANCE);
      }
      if (insurance.isMedicaid && this.medicaidPilotOn) {
        options.add(PaymentTypes.MEDICAID);
      }
      if (insurance.isCashPay) {
        options.add(PaymentTypes.CASH);
      }
    });

    options.forEach((name) => {
      this.availablePaymentTypes.push({ name, value: name });
    });
  }

  getInsurancePlanLabel(paymentPref: PaymentTypes): string {
    return `Your 
      ${paymentPref === PaymentTypes.EAP ? 'EAP' : ''} 
      ${paymentPref === PaymentTypes.MEDICARE ? 'Medicare' : ''} 
      ${paymentPref === PaymentTypes.MEDICAID ? 'Medicaid' : ''} 
      ${paymentPref !== PaymentTypes.MEDICARE && paymentPref !== PaymentTypes.MEDICAID ? 'insurance' : ''} plan`;
  }

  filterByInsuranceType(paymentPref: PaymentTypes): (insurance: IInsurance) => boolean {
    if (paymentPref === PaymentTypes.INSURANCE) {
      return (insurance: IInsurance) => insurance.isPrivateInsurance;
    }
    if (paymentPref === PaymentTypes.EAP) {
      return (insurance: IInsurance) => insurance.isEap;
    }
    if (paymentPref === PaymentTypes.MEDICARE) {
      return (insurance: IInsurance) => insurance.isMedicare;
    }
    if (paymentPref === PaymentTypes.MEDICAID) {
      return (insurance: IInsurance) => insurance.isMedicaid;
    }
    return (insurance: IInsurance) => insurance.isCashPay;
  }

  updateAcceptedInsurances(paymentType: PaymentTypes): void {
    const filterByType = this.filterByInsuranceType(paymentType);
    this.insuranceFormLabel = this.getInsurancePlanLabel(paymentType);

    this.acceptedInsurances = this.allInsurances
      .filter((insurance) => filterByType(insurance))
      .map((insurance) => ({ name: insurance.title, value: insurance.pncId }));

    // if cash pay we use the pnc ID of the self pay insurance. Else, we just reset insurance field
    const insurancePncId = paymentType === PaymentTypes.CASH
      ? this.allInsurances.find((x) => x.isCashPay)?.pncId ?? null
      : null;

    if (insurancePncId !== this.quickIntakeForm.controls.insurance.value) {
      this.quickIntakeForm.patchValue({
        insurance: insurancePncId
      });
    }

    if (this.acceptedInsurances.length > 0 && this.quickIntakeForm.controls.paymentType.value !== PaymentTypes.CASH) {
      this.showInsurancePlans = true;
      this.quickIntakeForm.controls.insurance.setValidators([Validators.required]);
      this.quickIntakeForm.controls.insurance.updateValueAndValidity({ emitEvent: false });
    } else {
      this.showInsurancePlans = false;
      this.quickIntakeForm.controls.insurance.clearValidators();
      this.quickIntakeForm.controls.insurance.updateValueAndValidity();
    }
  }

  resetPaymentTypeAndInsurance(disableFields: boolean = true): void {
    if (disableFields) {
      this.quickIntakeForm.controls.paymentType.disable({ emitEvent: true });
      this.quickIntakeForm.controls.insurance.disable({ emitEvent: false });
    }
    this.quickIntakeForm.patchValue({
      paymentType: null,
      insurance: null
    });
    this.showInsurancePlans = false;
  }

  formSubmitted(): void {
    this.currentFormState = QuickIntakeFormState.PROCESSING;
    this.fireFormSubmittedSegmentEvent();
    this.quickIntakeStore.submitIntake$(
      this.quickIntakeForm.value as QuickIntakeFormValues
    ).subscribe({
      next: () => {
        this.currentFormState = QuickIntakeFormState.SUBMITTED;
      },
      error: () => {
        this.currentFormState = QuickIntakeFormState.ERROR;
      }
    });
  }

  backToForm(): void {
    this.currentFormState = QuickIntakeFormState.FORM;
  }

  setupFormChangedSubscriptions(): void {
    Object.keys(this.quickIntakeForm.controls).forEach((key) => {
      this.setupControlSubscription(key, this.quickIntakeForm.get(key));
    });
  }

  transformFieldValueForSegment(fieldName: string, value: unknown): string | boolean | undefined {
    switch (fieldName as QuickIntakeFormKeys) {
      case 'insurance':
        return this.acceptedInsurances.find((x) => x.value === value)?.name;
      case 'genderPreference':
        return this.genderTypes.find((x) => x.value === value)?.name;
      case 'paymentType':
      case 'sessionFormatPreference':
      case 'contactSmsAllowed':
      case 'commPrefs':
      case 'medicationManagement':
      case 'readiness':
        return value as string | boolean;
      default:
        return undefined;
    }
  }

  setupControlSubscription(key: string, control: AbstractControl | null): void {
    if (!control) return;

    const isNestedGroup = control instanceof FormGroup && key !== 'location';
    const controls: Array<[string, AbstractControl]> = isNestedGroup
      ? Object.entries(control.controls)
      : [[key, control]];

    controls.forEach(([controlKey, ctrl]: [string, AbstractControl]) => {
      ctrl.valueChanges.pipe(
        filter((x) => !(x === null || x === undefined)),
        take(1),
        takeUntil(this.destroyed$)
      ).subscribe({
        next: (val) => {
          const eventData: IQuickLeadFormChangedData = { fieldName: controlKey };

          const transformedValue = this.transformFieldValueForSegment(key, val);
          if (transformedValue !== undefined) {
            eventData.value = transformedValue;
          }

          this.sendSegmentEvent(
            QuickIntakeSegmentEvents.QUICK_LEAD_FORM_CHANGED, eventData
          );
        },
      });
    });
  }

  fireFormStartedSegmentEvent(): void {
    this.sendSegmentEvent(QuickIntakeSegmentEvents.QUICK_LEAD_FORM_STARTED, {});
  }

  fireFormSubmittedSegmentEvent(): void {
    this.sendSegmentEvent(QuickIntakeSegmentEvents.QUICK_LEAD_FORM_SUBMITTED, {});
  }

  private sendSegmentEvent<T extends keyof IQuickIntakeSegmentSchema>(
    segmentEventType: T, data: IQuickIntakeSegmentSchema[T]
  ): void {
    this.segmentService.track<T>(segmentEventType, data).catch(() => {});
  }
}
